import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/20/solid';
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from '@headlessui/react';
import { VariantProps, cva, cx } from 'class-variance-authority';
export type ComboBoxOption = {
  value: string;
  label: string;
  id: string;
};
type Props = {
  options: ComboBoxOption[];
  allowEmpty?: boolean;
  onChange: (option: ComboBoxOption) => void;
  value?: ComboBoxOption['value'];
  autoComplete?: string;
  placeholder?: string;
  className?: string;
} & VariantProps<typeof comboBoxStyles>;

export const comboBoxStyles = cva(
  'w-full rounded-[1.5rem] leading-input text-[1.8rem] py-[1rem] px-[2.4rem] bg-[#F1F1F1] focus:bg-brand-white border-none focus:ring-0 focus:outline-2 focus:outline-brand-secondary placeholder:text-black h-[5.5rem] placeholder:text-zinc-500 font-medium',
  {
    variants: {
      error: {
        true: 'outline outline-2 outline-brand-red focus:outline-brand-red',
        false:
          'outline outline-2 outline-transparent focus:outline-brand-secondary',
      },
      defaultVariants: {
        error: 'false',
      },
    },
  }
);

const ComboBox = ({
  options,
  allowEmpty = true,
  error,
  onChange,
  value,
  autoComplete,
  placeholder,
  className,
}: Props) => {
  const [query, setQuery] = useState('');
  const [selectedOption, setSelectedOption] = useState<ComboBoxOption | null>(
    options.find((o) => o.value === value) ?? null
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  if (
    allowEmpty &&
    !!selectedOption?.value &&
    !options.some((o) => o.id === 'empty')
  ) {
    options.unshift({ id: 'empty', label: '', value: '' });
  }

  const onInputClick = () => {
    buttonRef.current?.click();
  };

  useEffect(() => {
    if (selectedOption !== null) {
      onChange(selectedOption);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  const filteredOptions = options.filter((options) => {
    return options.label.toLowerCase().includes(query.toLowerCase());
  });

  const onInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    // Detect autofill, match and select an option
    if (event.target.matches('*:-webkit-autofill')) {
      const foundOption = options.find((o) => o.label === val);
      if (foundOption) {
        setSelectedOption(foundOption);
        setTimeout(() => {
          buttonRef.current?.click();
        }, 0);
        return;
      }
    }
    setQuery(val);
  };

  return (
    <Combobox
      as="div"
      value={selectedOption}
      onChange={setSelectedOption}
      onClick={onInputClick}
      className={'w-full'}
      immediate
    >
      <div className={cx('relative', className)}>
        <ComboboxInput
          autoComplete={autoComplete}
          onChange={onInputChange}
          displayValue={(v: ComboBoxOption) => v?.label ?? ''}
          className={comboBoxStyles({ error })}
          placeholder={placeholder}
        />

        <ComboboxButton
          ref={buttonRef}
          className="absolute inset-y-0 right-[2rem] flex items-center rounded-r-md px-2 focus:outline-none"
        >
          <ChevronDownIcon
            className="h-12 w-12 text-zinc-700"
            aria-hidden="true"
          />
        </ComboboxButton>

        <ComboboxOptions className="absolute z-50 mt-4 max-h-96 w-full overflow-auto rounded-[1rem] bg-brand-white text-[1.4rem] shadow-lg ring-1 ring-brand-black ring-opacity-5 focus:outline-none">
          {filteredOptions.map((option) => (
            <ComboboxOption
              key={option.id}
              value={option}
              className={({ focus }) =>
                cx(
                  'relative flex h-16 select-none items-center pl-10 pr-4',
                  focus ? 'bg-brand-secondary-light' : ''
                )
              }
            >
              {({ selected }) => (
                <div className="flex w-full items-center justify-between">
                  <span
                    className={cx(
                      'block truncate text-[1.8rem]',
                      selected ? 'font-medium' : ''
                    )}
                  >
                    {option.label}
                  </span>

                  {selected && (
                    <span
                      className={cx(
                        'absolute inset-y-0 left-0 flex items-center pl-1.5 text-brand-secondary'
                      )}
                    >
                      <CheckIcon className="h-6 w-6" aria-hidden="true" />
                    </span>
                  )}
                </div>
              )}
            </ComboboxOption>
          ))}
        </ComboboxOptions>
      </div>
    </Combobox>
  );
};

export default ComboBox;
